import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  getBusinessProfiles,
  listBusinesses,
  setActiveBusiness,
  type ActiveBusinessPayload,
  type BusinessProfilesPayload,
  type BusinessProfileSummary,
} from "../../services/businessProfileService";
import {
  EMPTY_BUSINESS_PROFILES,
  enrichBusinessProfiles,
  normalizeBusinessList,
  normalizeBusinessProfilesPayload,
} from "./businessProfiles";
import {
  getAcceptedCurrencies,
  updateAcceptedCurrency,
  createDposSession,
  getDposSessionQr,
  getDposSessionReceipt,
  cancelDposSession,
  listDposSessions,
  getDposPerformance,
  getDposCashierModeStatus,
  setupDposCashierPin,
  verifyDposCashierPin,
  exitDposCashierMode,
  type CreateDposSessionRequest,
  type DposCashierModeStatus,
  type DposAcceptedCurrency,
  type DposSessionReceipt,
  type DposSessionDetails,
  type DposSessionListResponse,
  type ExitDposCashierModeRequest,
  type SetupDposCashierPinRequest,
  type UpdateAcceptedCurrencyRequest,
  type VerifyDposCashierPinRequest,
} from "../../services/dposService";
import {
  createPayLink,
  extractCreatedPayLinkFromApiResponse,
  listPayLinks,
  type CreatePayLinkBody,
  type ListPayLinksApiData,
} from "../../services/payLinksService";
import { mapApiPayLinkToRow } from "../../utils/mapApiPayLinkToRow";
import type { PaymentLinkRow } from "../../types/paymentLinksPanel";

export const businessProfilesQueryKey = ["businessProfiles"] as const;

const BUSINESS_CONTEXT_DEPENDENT_QUERY_PREFIXES = [
  ["acceptedCurrencies"],
  ["dposSessionQr"],
  ["dposSessionReceipt"],
  ["dposSessions"],
  ["dposPerformance"],
  ["dposCashierModeStatus"],
  ["payLinks"],
] as const;

const invalidateBusinessSuiteQueries = async (queryClient: QueryClient) => {
  await Promise.all([
    ...BUSINESS_CONTEXT_DEPENDENT_QUERY_PREFIXES.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey })
    ),
    queryClient.invalidateQueries({
      queryKey: ["businessWallet"],
      exact: false,
    }),
    queryClient.invalidateQueries({
      queryKey: ["businessAvailableAssets"],
      exact: false,
    }),
    queryClient.invalidateQueries({
      queryKey: ["businessAvailableBalances"],
      exact: false,
    }),
  ]);
};

// ─── Business profile ────────────────────────────────────────────────

export const useBusinessProfiles = () => {
  return useQuery({
    queryKey: businessProfilesQueryKey,
    queryFn: async () => {
      const [profilesResponse, businessListResponse] = await Promise.all([
        getBusinessProfiles(),
        listBusinesses().catch(() => null),
      ]);

      const profiles = normalizeBusinessProfilesPayload(
        profilesResponse?.data?.data
      );
      const businessList = normalizeBusinessList(
        businessListResponse?.data?.data
      );

      return enrichBusinessProfiles(profiles, businessList);
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useBusinessList = () => {
  const query = useBusinessProfiles();

  return {
    ...query,
    data: query.data?.businesses || [],
  };
};

export const useActiveBusiness = () => {
  const { data, isLoading, error, refetch, isFetching } = useBusinessProfiles();
  const businesses = data?.businesses || [];
  const activeBusinessId = data?.activeBusinessId || null;
  const activeBusiness =
    businesses.find(
      (business: BusinessProfileSummary) =>
        business.businessId === activeBusinessId
    ) || null;

  return {
    personal: data?.personal || EMPTY_BUSINESS_PROFILES.personal,
    businesses,
    activeBusinessId,
    business: activeBusiness,
    businessId: activeBusiness?.businessId || null,
    hasAvailableBusinesses: businesses.length > 0,
    hasSelectedBusiness: Boolean(activeBusiness?.businessId),
    selectionRequired: businesses.length > 0 && !activeBusiness?.businessId,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};

export const useSetActiveBusiness = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (businessId: string | null) => {
      const response = await setActiveBusiness(businessId);
      return (response?.data?.data || {
        activeBusinessId: null,
      }) as ActiveBusinessPayload;
    },
    onSuccess: async ({ activeBusinessId }) => {
      queryClient.setQueryData<BusinessProfilesPayload | undefined>(
        businessProfilesQueryKey,
        (previous) =>
          previous
            ? {
                ...previous,
                activeBusinessId,
              }
            : previous
      );

      await invalidateBusinessSuiteQueries(queryClient);
    },
  });
};

// ─── Accepted currencies ─────────────────────────────────────────────

export const useAcceptedCurrencies = (businessId: string | null) => {
  return useQuery({
    queryKey: ["acceptedCurrencies", businessId],
    queryFn: async () => {
      if (!businessId) return [] as DposAcceptedCurrency[];
      const response = await getAcceptedCurrencies(businessId);
      return (response?.data?.data?.currencies || []) as DposAcceptedCurrency[];
    },
    enabled: !!businessId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdateAcceptedCurrency = (businessId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateAcceptedCurrencyRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      const response = await updateAcceptedCurrency(businessId, data);
      return response?.data?.data?.currencies || [];
    },
    onSuccess: (currencies) => {
      // Optimistically update cache with server response
      queryClient.setQueryData(["acceptedCurrencies", businessId], currencies);
    },
    onError: () => {
      // Refetch on error to ensure consistent state
      queryClient.invalidateQueries({
        queryKey: ["acceptedCurrencies", businessId],
      });
    },
  });
};

// ─── DPOS sessions ───────────────────────────────────────────────────

export const useCreateDposSession = (businessId: string | null) => {
  return useMutation({
    mutationFn: async (data: CreateDposSessionRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      const response = await createDposSession(businessId, data);
      return (response?.data?.data || null) as DposSessionDetails | null;
    },
  });
};

export const useDposSessionQr = (
  businessId: string | null,
  sessionId: string | null
) => {
  return useQuery({
    queryKey: ["dposSessionQr", businessId, sessionId],
    queryFn: async () => {
      if (!businessId || !sessionId) return null;
      const response = await getDposSessionQr(businessId, sessionId);
      return (response?.data?.data || null) as DposSessionDetails | null;
    },
    enabled: !!businessId && !!sessionId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (
        status === "paid" ||
        status === "confirmed" ||
        status === "expired" ||
        status === "cancelled" ||
        status === "failed"
      )
        return false;
      return 5000;
    },
  });
};

export const useDposSessionReceipt = (
  businessId: string | null,
  sessionId: string | null
) => {
  return useQuery({
    queryKey: ["dposSessionReceipt", businessId, sessionId],
    queryFn: async () => {
      if (!businessId || !sessionId) return null;
      const response = await getDposSessionReceipt(businessId, sessionId);
      return (response?.data?.data || null) as DposSessionReceipt | null;
    },
    enabled: !!businessId && !!sessionId,
    staleTime: 1000 * 60,
  });
};

export const useDposSessions = (
  businessId: string | null,
  params: {
    page?: number;
    limit?: number;
    status?: string;
    sort?: string;
    search?: string;
  } = {}
) => {
  return useQuery({
    queryKey: ["dposSessions", businessId, params],
    queryFn: async () => {
      if (!businessId)
        return {
          totalItems: 0,
          totalPages: 0,
          currentPage: 1,
          limit: 20,
          results: [],
        } as DposSessionListResponse;
      const response = await listDposSessions(businessId, params);
      return (response?.data?.data || {
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
        limit: 20,
        results: [],
      }) as DposSessionListResponse;
    },
    enabled: !!businessId,
    staleTime: 1000 * 30,
  });
};

export const useDposPerformance = (
  businessId: string | null,
  period: string = "1w"
) => {
  return useQuery({
    queryKey: ["dposPerformance", businessId, period],
    queryFn: async () => {
      if (!businessId) return null;
      const response = await getDposPerformance(businessId, period);
      return response?.data?.data || null;
    },
    enabled: !!businessId,
    staleTime: 1000 * 60,
  });
};

export const useCancelDposSession = (businessId: string | null) => {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      if (!businessId) throw new Error("Business ID is required");
      const response = await cancelDposSession(businessId, sessionId);
      return response?.data?.data;
    },
  });
};

export const payLinksQueryKey = (businessId: string | null) =>
  ["payLinks", businessId] as const;

export const useCreatePayLink = (businessId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreatePayLinkBody) => {
      if (!businessId) {
        throw new Error("Business ID is required");
      }
      try {
        const response = await createPayLink(businessId, body);
        const link = extractCreatedPayLinkFromApiResponse(response);
        if (!link?.payLinkUrl) {
          const msg =
            (response?.data as { message?: string } | undefined)?.message ??
            "Could not create payment link";
          throw new Error(msg);
        }
        return link;
      } catch (e: unknown) {
        if (e instanceof Error && !("response" in e)) {
          throw e;
        }
        const ax = e as { response?: { data?: { message?: string } } };
        const msg =
          ax.response?.data?.message ||
          (e instanceof Error ? e.message : null) ||
          "Could not create payment link";
        throw new Error(
          typeof msg === "string" ? msg : "Could not create payment link"
        );
      }
    },
    onSuccess: () => {
      if (businessId) {
        void queryClient.invalidateQueries({
          queryKey: ["payLinks", businessId],
        });
      }
    },
  });
};

export const usePayLinks = (
  businessId: string | null,
  options: { page?: number; limit?: number; enabled?: boolean } = {}
) => {
  const { page = 1, limit = 20, enabled = true } = options;

  return useQuery({
    queryKey: [...payLinksQueryKey(businessId), page, limit],
    queryFn: async () => {
      if (!businessId) {
        return {
          rows: [] as PaymentLinkRow[],
          pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
        };
      }
      const response = await listPayLinks(businessId, { page, limit });
      const payload = response?.data as
        | { status?: boolean; data?: ListPayLinksApiData }
        | undefined;
      const data = payload?.data;
      const raw = Array.isArray(data?.payLinks) ? data.payLinks : [];
      const rows = raw.map(mapApiPayLinkToRow);
      const pagination = data?.pagination ?? {
        page: 1,
        limit: 20,
        total: rows.length,
        totalPages: 1,
      };
      return { rows, pagination };
    },
    enabled: Boolean(businessId) && enabled,
    staleTime: 1000 * 30,
  });
};

export const useDposCashierModeStatus = (businessId: string | null) => {
  return useQuery({
    queryKey: ["dposCashierModeStatus", businessId],
    queryFn: async () => {
      if (!businessId) {
        return {
          hasPin: false,
          pinLength: null,
          lockedUntil: null,
          isLocked: false,
          session: null,
        } as DposCashierModeStatus;
      }

      const response = await getDposCashierModeStatus(businessId);
      return (response?.data?.data || {
        hasPin: false,
        pinLength: null,
        lockedUntil: null,
        isLocked: false,
        session: null,
      }) as DposCashierModeStatus;
    },
    enabled: !!businessId,
    refetchInterval: (query) =>
      query.state.data?.session?.status === "active" ? 60 * 1000 : false,
    staleTime: 1000 * 30,
  });
};

export const useSetupDposCashierPin = (businessId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SetupDposCashierPinRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      const response = await setupDposCashierPin(businessId, data);
      return (response?.data?.data || null) as DposCashierModeStatus | null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dposCashierModeStatus", businessId],
      });
    },
  });
};

export const useVerifyDposCashierPin = (businessId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: VerifyDposCashierPinRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      const response = await verifyDposCashierPin(businessId, data);
      return (response?.data?.data || null) as DposCashierModeStatus | null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dposCashierModeStatus", businessId],
      });
    },
  });
};

export const useExitDposCashierMode = (businessId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ExitDposCashierModeRequest) => {
      if (!businessId) throw new Error("Business ID is required");
      const response = await exitDposCashierMode(businessId, data);
      return (response?.data?.data || null) as DposCashierModeStatus | null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["dposCashierModeStatus", businessId],
      });
    },
  });
};
